import { createAction, createSlice } from '@reduxjs/toolkit';
import { apiBaseUrl } from '../util/api';
import { resolveLinkupAttendeeCount } from '../util/linkupData';

/**
 * Add user to Stream Chat channel
 * This function calls the backend to add a user to a Stream Chat channel
 */
const addUserToStreamChatChannel = async (linkUpId, userId) => {
  const url = `${apiBaseUrl()}/api/stream-chat/add-member`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      linkUpId,
      userId,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Failed to add user to Stream Chat channel');
      }
    });
};

/**
 * Remove user from Stream Chat channel
 * This function calls the backend to remove a user from a Stream Chat channel
 */
const removeUserFromStreamChatChannel = async (linkUpId, userId) => {
  const url = `${apiBaseUrl()}/api/stream-chat/remove-member`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      linkUpId,
      userId,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Failed to remove user from Stream Chat channel');
      }
    });
};

// Action Types
export const FETCH_LINKUPS_REQUEST = 'app/linkups/FETCH_LINKUPS_REQUEST';
export const FETCH_LINKUPS_SUCCESS = 'app/linkups/FETCH_LINKUPS_SUCCESS';
export const FETCH_LINKUPS_ERROR = 'app/linkups/FETCH_LINKUPS_ERROR';

export const FETCH_USER_LINKUPS_REQUEST = 'app/linkups/FETCH_USER_LINKUPS_REQUEST';
export const FETCH_USER_LINKUPS_SUCCESS = 'app/linkups/FETCH_USER_LINKUPS_SUCCESS';
export const FETCH_USER_LINKUPS_ERROR = 'app/linkups/FETCH_USER_LINKUPS_ERROR';

export const CREATE_LINKUP_REQUEST = 'app/linkups/CREATE_LINKUP_REQUEST';
export const CREATE_LINKUP_SUCCESS = 'app/linkups/CREATE_LINKUP_SUCCESS';
export const CREATE_LINKUP_ERROR = 'app/linkups/CREATE_LINKUP_ERROR';

export const FETCH_LINKUP_DETAILS_REQUEST = 'app/linkups/FETCH_LINKUP_DETAILS_REQUEST';
export const FETCH_LINKUP_DETAILS_SUCCESS = 'app/linkups/FETCH_LINKUP_DETAILS_SUCCESS';
export const FETCH_LINKUP_DETAILS_ERROR = 'app/linkups/FETCH_LINKUP_DETAILS_ERROR';

export const JOIN_LINKUP_REQUEST = 'app/linkups/JOIN_LINKUP_REQUEST';
export const JOIN_LINKUP_SUCCESS = 'app/linkups/JOIN_LINKUP_SUCCESS';
export const JOIN_LINKUP_ERROR = 'app/linkups/JOIN_LINKUP_ERROR';

export const LEAVE_LINKUP_REQUEST = 'app/linkups/LEAVE_LINKUP_REQUEST';
export const LEAVE_LINKUP_SUCCESS = 'app/linkups/LEAVE_LINKUP_SUCCESS';
export const LEAVE_LINKUP_ERROR = 'app/linkups/LEAVE_LINKUP_ERROR';

export const SEARCH_LINKUPS_REQUEST = 'app/linkups/SEARCH_LINKUPS_REQUEST';
export const SEARCH_LINKUPS_SUCCESS = 'app/linkups/SEARCH_LINKUPS_SUCCESS';
export const SEARCH_LINKUPS_ERROR = 'app/linkups/SEARCH_LINKUPS_ERROR';

// Unread counts
export const UPDATE_UNREAD_COUNTS = 'app/linkups/UPDATE_UNREAD_COUNTS';
export const SET_TOTAL_UNREAD_COUNT = 'app/linkups/SET_TOTAL_UNREAD_COUNT';
export const INCREMENT_UNREAD_COUNT = 'app/linkups/INCREMENT_UNREAD_COUNT';
export const CLEAR_UNREAD_COUNT = 'app/linkups/CLEAR_UNREAD_COUNT';

// Initial State
export const initialState = {
  // All linkups (discover/feed)
  linkups: [],
  fetchLinkupsInProgress: false,
  fetchLinkupsError: null,
  linkupsLastFetched: null,
  linkupsQueryKey: null,

  // User's linkups (created/joined)
  userLinkups: [],
  fetchUserLinkupsInProgress: false,
  fetchUserLinkupsError: null,
  userLinkupsLastFetched: null,
  userLinkupsUserId: null,

  // Linkup creation
  createLinkupInProgress: false,
  createLinkupError: null,

  // Linkup details
  linkupDetails: {},
  fetchLinkupDetailsInProgress: false,
  fetchLinkupDetailsError: null,

  // Linkup actions
  joinLinkupInProgress: false,
  joinLinkupError: null,
  leaveLinkupInProgress: false,
  leaveLinkupError: null,

  // Search
  searchResults: [],
  searchInProgress: false,
  searchError: null,

  // Unread counts for LinkUp chats
  unreadCounts: {}, // { linkUpId: count }
  totalUnreadCount: 0,
};

const fetchLinkupsRequestAction = createAction(FETCH_LINKUPS_REQUEST);
const fetchLinkupsSuccessAction = createAction(FETCH_LINKUPS_SUCCESS);
const fetchLinkupsErrorAction = createAction(FETCH_LINKUPS_ERROR);

const fetchUserLinkupsRequestAction = createAction(FETCH_USER_LINKUPS_REQUEST);
const fetchUserLinkupsSuccessAction = createAction(FETCH_USER_LINKUPS_SUCCESS);
const fetchUserLinkupsErrorAction = createAction(FETCH_USER_LINKUPS_ERROR);

const createLinkupRequestAction = createAction(CREATE_LINKUP_REQUEST);
const createLinkupSuccessAction = createAction(CREATE_LINKUP_SUCCESS);
const createLinkupErrorAction = createAction(CREATE_LINKUP_ERROR);

const fetchLinkupDetailsRequestAction = createAction(FETCH_LINKUP_DETAILS_REQUEST);
const fetchLinkupDetailsSuccessAction = createAction(FETCH_LINKUP_DETAILS_SUCCESS);
const fetchLinkupDetailsErrorAction = createAction(FETCH_LINKUP_DETAILS_ERROR);

const joinLinkupRequestAction = createAction(JOIN_LINKUP_REQUEST);
const joinLinkupSuccessAction = createAction(JOIN_LINKUP_SUCCESS);
const joinLinkupErrorAction = createAction(JOIN_LINKUP_ERROR);

const leaveLinkupRequestAction = createAction(LEAVE_LINKUP_REQUEST);
const leaveLinkupSuccessAction = createAction(LEAVE_LINKUP_SUCCESS);
const leaveLinkupErrorAction = createAction(LEAVE_LINKUP_ERROR);

const searchLinkupsRequestAction = createAction(SEARCH_LINKUPS_REQUEST);
const searchLinkupsSuccessAction = createAction(SEARCH_LINKUPS_SUCCESS);
const searchLinkupsErrorAction = createAction(SEARCH_LINKUPS_ERROR);

const updateUnreadCountsAction = createAction(UPDATE_UNREAD_COUNTS);
const setTotalUnreadCountAction = createAction(SET_TOTAL_UNREAD_COUNT);
const incrementUnreadCountAction = createAction(INCREMENT_UNREAD_COUNT);
const clearUnreadCountAction = createAction(CLEAR_UNREAD_COUNT);

const sumUnreadCounts = counts => Object.values(counts).reduce((sum, count) => sum + count, 0);

const linkupsSlice = createSlice({
  name: 'linkups',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchLinkupsRequestAction, state => {
        state.fetchLinkupsInProgress = true;
        state.fetchLinkupsError = null;
      })
      .addCase(fetchLinkupsSuccessAction, (state, action) => {
        state.fetchLinkupsInProgress = false;
        state.linkups = action.payload.linkups;
        state.linkupsLastFetched = Date.now();
        state.linkupsQueryKey = action.payload.queryKey;
      })
      .addCase(fetchLinkupsErrorAction, (state, action) => {
        state.fetchLinkupsInProgress = false;
        state.fetchLinkupsError = action.payload.error;
      })
      .addCase(fetchUserLinkupsRequestAction, state => {
        state.fetchUserLinkupsInProgress = true;
        state.fetchUserLinkupsError = null;
      })
      .addCase(fetchUserLinkupsSuccessAction, (state, action) => {
        state.fetchUserLinkupsInProgress = false;
        state.userLinkups = action.payload.linkups;
        state.userLinkupsLastFetched = Date.now();
        state.userLinkupsUserId = action.payload.userId;
      })
      .addCase(fetchUserLinkupsErrorAction, (state, action) => {
        state.fetchUserLinkupsInProgress = false;
        state.fetchUserLinkupsError = action.payload.error;
      })
      .addCase(createLinkupRequestAction, state => {
        state.createLinkupInProgress = true;
        state.createLinkupError = null;
      })
      .addCase(createLinkupSuccessAction, (state, action) => {
        state.createLinkupInProgress = false;
        state.userLinkups.push(action.payload.linkup);
      })
      .addCase(createLinkupErrorAction, (state, action) => {
        state.createLinkupInProgress = false;
        state.createLinkupError = action.payload.error;
      })
      .addCase(fetchLinkupDetailsRequestAction, state => {
        state.fetchLinkupDetailsInProgress = true;
        state.fetchLinkupDetailsError = null;
      })
      .addCase(fetchLinkupDetailsSuccessAction, (state, action) => {
        state.fetchLinkupDetailsInProgress = false;
        state.linkupDetails[action.payload.linkup.id] = action.payload.linkup;
      })
      .addCase(fetchLinkupDetailsErrorAction, (state, action) => {
        state.fetchLinkupDetailsInProgress = false;
        state.fetchLinkupDetailsError = action.payload.error;
      })
      .addCase(joinLinkupRequestAction, state => {
        state.joinLinkupInProgress = true;
        state.joinLinkupError = null;
      })
      .addCase(joinLinkupSuccessAction, (state, action) => {
        state.joinLinkupInProgress = false;
        state.userLinkups.push(action.payload.linkup);
      })
      .addCase(joinLinkupErrorAction, (state, action) => {
        state.joinLinkupInProgress = false;
        state.joinLinkupError = action.payload.error;
      })
      .addCase(leaveLinkupRequestAction, state => {
        state.leaveLinkupInProgress = true;
        state.leaveLinkupError = null;
      })
      .addCase(leaveLinkupSuccessAction, (state, action) => {
        state.leaveLinkupInProgress = false;
        state.userLinkups = state.userLinkups.filter(l => l.id !== action.payload.linkupId);
      })
      .addCase(leaveLinkupErrorAction, (state, action) => {
        state.leaveLinkupInProgress = false;
        state.leaveLinkupError = action.payload.error;
      })
      .addCase(searchLinkupsRequestAction, state => {
        state.searchInProgress = true;
        state.searchError = null;
      })
      .addCase(searchLinkupsSuccessAction, (state, action) => {
        state.searchInProgress = false;
        state.searchResults = action.payload.linkups;
      })
      .addCase(searchLinkupsErrorAction, (state, action) => {
        state.searchInProgress = false;
        state.searchError = action.payload.error;
      })
      .addCase(updateUnreadCountsAction, (state, action) => {
        state.unreadCounts = action.payload.counts;
        state.totalUnreadCount = sumUnreadCounts(action.payload.counts);
      })
      .addCase(setTotalUnreadCountAction, (state, action) => {
        state.totalUnreadCount = action.payload.count;
      })
      .addCase(incrementUnreadCountAction, (state, action) => {
        state.unreadCounts[action.payload.linkUpId] =
          (state.unreadCounts[action.payload.linkUpId] || 0) + 1;
        state.totalUnreadCount = sumUnreadCounts(state.unreadCounts);
      })
      .addCase(clearUnreadCountAction, (state, action) => {
        delete state.unreadCounts[action.payload.linkUpId];
        state.totalUnreadCount = sumUnreadCounts(state.unreadCounts);
      });
  },
});

export default linkupsSlice.reducer;

// Action Creators
export const fetchLinkupsRequest = () => fetchLinkupsRequestAction();
export const fetchLinkupsSuccess = (linkups, queryKey) =>
  fetchLinkupsSuccessAction({ linkups, queryKey });
export const fetchLinkupsError = error => fetchLinkupsErrorAction({ error });

export const fetchUserLinkupsRequest = () => fetchUserLinkupsRequestAction();
export const fetchUserLinkupsSuccess = (linkups, userId) =>
  fetchUserLinkupsSuccessAction({ linkups, userId });
export const fetchUserLinkupsError = error => fetchUserLinkupsErrorAction({ error });

export const createLinkupRequest = () => createLinkupRequestAction();
export const createLinkupSuccess = linkup => createLinkupSuccessAction({ linkup });
export const createLinkupError = error => createLinkupErrorAction({ error });

export const fetchLinkupDetailsRequest = () => fetchLinkupDetailsRequestAction();
export const fetchLinkupDetailsSuccess = linkup => fetchLinkupDetailsSuccessAction({ linkup });
export const fetchLinkupDetailsError = error => fetchLinkupDetailsErrorAction({ error });

export const joinLinkupRequest = () => joinLinkupRequestAction();
export const joinLinkupSuccess = linkup => joinLinkupSuccessAction({ linkup });
export const joinLinkupError = error => joinLinkupErrorAction({ error });

export const leaveLinkupRequest = () => leaveLinkupRequestAction();
export const leaveLinkupSuccess = linkupId => leaveLinkupSuccessAction({ linkupId });
export const leaveLinkupError = error => leaveLinkupErrorAction({ error });

export const searchLinkupsRequest = () => searchLinkupsRequestAction();
export const searchLinkupsSuccess = linkups => searchLinkupsSuccessAction({ linkups });
export const searchLinkupsError = error => searchLinkupsErrorAction({ error });

// Unread count action creators
export const updateUnreadCounts = counts => updateUnreadCountsAction({ counts });
export const setTotalUnreadCount = count => setTotalUnreadCountAction({ count });
export const incrementUnreadCount = linkUpId => incrementUnreadCountAction({ linkUpId });
export const clearUnreadCount = linkUpId => clearUnreadCountAction({ linkUpId });

// Thunks
const LINKUPS_CACHE_DURATION = 60 * 1000;
const USER_LINKUPS_CACHE_DURATION = 30 * 1000;
const linkupRequests = new Map();
const userLinkupRequests = new Map();

const createQueryKey = filters => {
  const entries = Object.entries(filters || {}).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
};

const deriveCityFromAddress = addressValue => {
  if (!addressValue || typeof addressValue !== 'string') {
    return '';
  }

  const parts = addressValue
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return parts[1];
  }

  if (parts.length === 2) {
    return parts[0];
  }

  return '';
};

export const fetchLinkups = (filters = {}, fetchOptions = {}) => (dispatch, getState) => {
  const { force = false } = fetchOptions;
  const state = getState();
  const queryKey = createQueryKey(filters);
  const isSameQuery = state.linkups?.linkupsQueryKey === queryKey;
  const lastFetched = state.linkups?.linkupsLastFetched;
  const hasFreshCache =
    isSameQuery &&
    lastFetched &&
    Date.now() - lastFetched < LINKUPS_CACHE_DURATION &&
    Array.isArray(state.linkups?.linkups);

  if (!force && hasFreshCache) {
    return Promise.resolve(state.linkups.linkups);
  }

  if (!force && linkupRequests.has(queryKey)) {
    return linkupRequests.get(queryKey);
  }

  dispatch(fetchLinkupsRequest());

  const params = new URLSearchParams(filters);
  const url = `${apiBaseUrl()}/api/supabase/linkups/discover${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  const requestPromise = fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        const linkups = data.linkups || [];
        dispatch(fetchLinkupsSuccess(linkups, queryKey));
        return linkups;
      } else {
        throw new Error(data.message || 'Failed to fetch linkups');
      }
    })
    .catch(error => {
      console.error('Error fetching linkups:', error);
      dispatch(fetchLinkupsError(error.message));
      throw error;
    })
    .finally(() => {
      linkupRequests.delete(queryKey);
    });

  linkupRequests.set(queryKey, requestPromise);
  return requestPromise;
};

export const fetchUserLinkups = (userId, fetchOptions = {}) => (dispatch, getState) => {
  // If no userId provided, silently return empty array
  // This prevents errors when page loads without authentication
  if (!userId) {
    return Promise.resolve([]);
  }

  const { force = false } = fetchOptions;
  const state = getState();
  const sameUser = state.linkups?.userLinkupsUserId === userId;
  const lastFetched = state.linkups?.userLinkupsLastFetched;
  const hasFreshCache =
    sameUser &&
    lastFetched &&
    Date.now() - lastFetched < USER_LINKUPS_CACHE_DURATION &&
    Array.isArray(state.linkups?.userLinkups);

  if (!force && hasFreshCache) {
    return Promise.resolve(state.linkups.userLinkups);
  }

  if (!force && userLinkupRequests.has(userId)) {
    return userLinkupRequests.get(userId);
  }

  dispatch(fetchUserLinkupsRequest());

  // Use the membership API to get user's joined LinkUps
  const url = `${apiBaseUrl()}/api/supabase/linkups/memberships?user_id=${userId}`;

  const requestPromise = fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        console.error('❌ fetchUserLinkups: HTTP error! status:', response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        // Transform membership data to LinkUp format for the UI
        const linkups = (data.data || [])
          .map(membership => {
            const linkUp = membership.LinkUps; // Note: Backend returns 'LinkUps' (capitalized)

            if (!linkUp) {
              console.warn('⚠️ Membership has no LinkUp data:', membership);
              return null;
            }

            const attendeeCount = resolveLinkupAttendeeCount(linkUp);
            const transformed = {
              id: linkUp.id, // Backend selects 'id' from LinkUps table
              LinkUp_id: membership.LinkUp_id, // Also store the membership's LinkUp_id for flexibility
              title: linkUp.linkUp_name,
              description: linkUp.linkUp_description,
              location: linkUp.location_description || linkUp.linkUp_address,
              linkUp_address: linkUp.linkUp_address || '',
              city: deriveCityFromAddress(linkUp.linkUp_address),
              date: linkUp.linkUp_date,
              // Handle time and timezone exactly like date - direct assignment
              // Backend always sends these fields (as null if database has NULL)
              time: linkUp.time,
              timezone: linkUp.timezone,
              attendeeCount,
              totalGoing: attendeeCount,
              members_count: attendeeCount,
              category: linkUp.linkUp_category,
              status: linkUp.linkUp_status,
              crewId: linkUp.crew_id,
              createdBy: linkUp.created_by,
              fanLimit: linkUp.linkUp_fan_limit,
              linkUpType: linkUp.linkUp_type || 'public',
              locationDescription: linkUp.location_description || '',
              // Add UI-specific fields for chat interface
              image: linkUp.linkUp_header_image || '/assets/linkup.svg',
              lastMessage: 'Welcome to the LinkUp!',
              lastMessageTime: '12:00 PM',
              unreadCount: 0,
              isFavorite: false,
              isArchived: linkUp.linkUp_status === 'archived', // Set based on status
              // Store membership info
              membershipId: membership.id,
              membershipStatus: membership.user_status,
            };

            return transformed;
          })
          .filter(Boolean); // Remove any null entries

        dispatch(fetchUserLinkupsSuccess(linkups, userId));
      } else {
        throw new Error(data.message || 'Failed to fetch user linkup memberships');
      }
    })
    .catch(error => {
      console.error('Error fetching user linkup memberships:', error);
      dispatch(fetchUserLinkupsError(error.message));
      throw error;
    })
    .finally(() => {
      userLinkupRequests.delete(userId);
    });

  userLinkupRequests.set(userId, requestPromise);
  return requestPromise;
};

export const joinLinkUp = (linkUpId, userId) => dispatch => {
  dispatch(joinLinkupRequest());

  const url = `${apiBaseUrl()}/api/supabase/linkups/join`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      linkUpId,
      userId,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        dispatch(joinLinkupSuccess(data.membership));

        // Add user to Stream Chat channel after successful database join
        return addUserToStreamChatChannel(linkUpId, userId)
          .then(() => {
            // Refetch user's LinkUp memberships to update the UI with latest data
            return dispatch(fetchUserLinkups(userId, { force: true })).then(() => {
              return data.membership;
            });
          })
          .catch(error => {
            console.error('⚠️ Failed to add user to Stream Chat channel:', error);
            // Still refetch memberships even if Stream Chat fails
            return dispatch(fetchUserLinkups(userId, { force: true })).then(() => {
              return data.membership;
            });
          });
      } else {
        throw new Error(data.message || 'Failed to join LinkUp');
      }
    })
    .catch(error => {
      console.error('Error joining LinkUp:', error);
      dispatch(joinLinkupError(error.message));
      throw error;
    });
};

export const leaveLinkUp = (linkUpId, userId) => dispatch => {
  dispatch(leaveLinkupRequest());

  const url = `${apiBaseUrl()}/api/supabase/linkups/leave`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      linkUpId,
      userId,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        dispatch(leaveLinkupSuccess({ linkupId: linkUpId }));

        // Remove user from Stream Chat channel after successful database leave
        return removeUserFromStreamChatChannel(linkUpId, userId)
          .then(() => {
            // Refetch user's LinkUp memberships to update the UI with latest data
            return dispatch(fetchUserLinkups(userId, { force: true })).then(() => {
              return data.membership;
            });
          })
          .catch(error => {
            console.error('⚠️ Failed to remove user from Stream Chat channel:', error);
            // Still refetch memberships even if Stream Chat fails
            return dispatch(fetchUserLinkups(userId, { force: true })).then(() => {
              return data.membership;
            });
          });
      } else {
        throw new Error(data.message || 'Failed to leave LinkUp');
      }
    })
    .catch(error => {
      console.error('Error leaving LinkUp:', error);
      dispatch(leaveLinkupError(error.message));
      throw error;
    });
};

export const createLinkup = linkupData => (dispatch, getState) => {
  dispatch(createLinkupRequest());

  const url = `${apiBaseUrl()}/api/supabase/linkups/create`;

  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(linkupData),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      // Handle moderation rejection (HTTP 200 but success: false)
      if (data.moderation_rejected) {
        dispatch(
          createLinkupError(
            data.moderation_unavailable
              ? 'Content moderation is temporarily unavailable. Your LinkUp was not created. Please try again later.'
              : 'Content may violate community guidelines'
          )
        );
        return data; // Return full response so component can check moderation_rejected
      }

      if (data.success) {
        dispatch(createLinkupSuccess(data.linkup));

        const currentUserId = getState().user?.currentUser?.id?.uuid;
        const creatorUserId = currentUserId || linkupData.created_by;
        const createdLinkupId = data.linkup?.id;

        if (!creatorUserId || !createdLinkupId) {
          return data;
        }

        // Ensure the creator is immediately able to access the LinkUp chat.
        return addUserToStreamChatChannel(createdLinkupId, creatorUserId)
          .catch(error => {
            console.error('⚠️ Failed to add LinkUp creator to Stream Chat channel:', error);
          })
          .then(() =>
            dispatch(fetchUserLinkups(creatorUserId, { force: true })).catch(error => {
              console.error('⚠️ Failed to refresh LinkUp memberships after create:', error);
            })
          )
          .then(() => data);
      } else {
        throw new Error(data.message || 'Failed to create linkup');
      }
    })
    .catch(error => {
      console.error('Error creating linkup:', error);
      dispatch(createLinkupError(error.message));
      throw error;
    });
};

export const fetchLinkupDetails = linkupId => dispatch => {
  dispatch(fetchLinkupDetailsRequest());

  const url = `${apiBaseUrl()}/api/supabase/linkups/${linkupId}`;

  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        dispatch(fetchLinkupDetailsSuccess(data.linkup));
      } else {
        throw new Error(data.message || 'Failed to fetch linkup details');
      }
    })
    .catch(error => {
      console.error('Error fetching linkup details:', error);
      dispatch(fetchLinkupDetailsError(error.message));
      throw error;
    });
};

export const searchLinkups = (query, filters = {}) => dispatch => {
  dispatch(searchLinkupsRequest());

  const params = new URLSearchParams({ q: query, ...filters });
  const url = `${apiBaseUrl()}/api/supabase/linkups/search?${params.toString()}`;

  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        dispatch(searchLinkupsSuccess(data.linkups || data.results || []));
      } else {
        throw new Error(data.message || 'Failed to search linkups');
      }
    })
    .catch(error => {
      console.error('Error searching linkups:', error);
      dispatch(searchLinkupsError(error.message));
      throw error;
    });
};

// Selectors
export const getLinkups = state => state.linkups?.linkups || [];
export const getUserLinkups = state => state.linkups?.userLinkups || [];
export const getLinkupDetails = (state, linkupId) => state.linkups?.linkupDetails[linkupId] || null;
export const getSearchResults = state => state.linkups?.searchResults || [];
export const isCreatingLinkup = state => state.linkups?.createLinkupInProgress || false;
export const getCreateLinkupError = state => state.linkups?.createLinkupError || null;
export const isFetchingLinkups = state => state.linkups?.fetchLinkupsInProgress || false;

// User LinkUps selectors
export const isFetchingUserLinkups = state => state.linkups?.fetchUserLinkupsInProgress || false;
export const getFetchUserLinkupsError = state => state.linkups?.fetchUserLinkupsError || null;

// Unread counts selectors
export const getUnreadCounts = state => state.linkups?.unreadCounts || {};
export const getTotalUnreadCount = state => state.linkups?.totalUnreadCount || 0;
export const getUnreadCountForLinkUp = (state, linkUpId) =>
  state.linkups?.unreadCounts[linkUpId] || 0;
